import { useRef, useState } from "react";
import { Upload, X, Star, Image as ImageIcon, Link as LinkIcon, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

const MAX_DIM = 1600;
const QUALITY = 0.82;

async function compressImage(file) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 500 * 1024) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", QUALITY));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" });
  } catch {
    return file;
  }
}

export default function PhotoUploader({ urls, onChange, testIdPrefix = "photo" }) {
  const fileRef = useRef(null);
  const dragIdx = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [queue, setQueue] = useState([]);
  const [urlInput, setUrlInput] = useState("");
  const [showUrlInput, setShowUrlInput] = useState(false);

  const uploadFiles = async (fileList) => {
    const all = Array.from(fileList || []);
    const files = all.filter((f) => f.type.startsWith("image/"));
    if (all.length > files.length) toast.warning(`${all.length - files.length} unsupported file(s) skipped`);
    if (files.length === 0) return;
    const items = files.map((f, i) => ({ id: `${Date.now()}-${i}`, name: f.name, progress: 0, status: "compressing" }));
    setQueue((q) => [...q, ...items]);

    let done = 0;
    const results = new Array(files.length).fill(null);
    await Promise.all(files.map(async (file, i) => {
      const id = items[i].id;
      try {
        const compressed = await compressImage(file);
        setQueue((q) => q.map((it) => (it.id === id ? { ...it, status: "uploading" } : it)));
        const fd = new FormData();
        fd.append("files", compressed);
        const r = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (ev) => {
            const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 50;
            setQueue((q) => q.map((it) => (it.id === id ? { ...it, progress: pct } : it)));
          },
        });
        const url = r.data.files?.[0]?.url;
        if (url) {
          results[i] = url;
          done += 1;
          setQueue((q) => q.map((it) => (it.id === id ? { ...it, status: "done", progress: 100 } : it)));
        } else {
          setQueue((q) => q.map((it) => (it.id === id ? { ...it, status: "error" } : it)));
        }
      } catch {
        setQueue((q) => q.map((it) => (it.id === id ? { ...it, status: "error" } : it)));
      }
    }));

    const ordered = results.filter(Boolean);
    if (ordered.length) onChange((prev) => [...prev, ...ordered]);
    setQueue((q) => q.filter((it) => it.status !== "done"));

    if (done > 0) toast.success(`${done} photo${done > 1 ? "s" : ""} uploaded`);
    if (done < files.length) toast.error(`${files.length - done} photo(s) failed`);
    setTimeout(() => setQueue((q) => q.filter((it) => it.status !== "error")), 4000);
  };

  const onDropFiles = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const move = (from, to) => {
    if (from === null || from === to) return;
    onChange((prev) => {
      const next = [...prev];
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return next;
    });
  };

  const setCover = (i) => {
    onChange((prev) => {
      const next = [...prev];
      const [m] = next.splice(i, 1);
      next.unshift(m);
      return next;
    });
    toast.success("Cover photo set");
  };

  const remove = (i) => onChange((prev) => prev.filter((_, idx) => idx !== i));

  const addUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    onChange((prev) => [...prev, u]);
    setUrlInput("");
    setShowUrlInput(false);
  };

  return (
    <div data-testid={`${testIdPrefix}-uploader`}>
      <div className="flex items-center justify-between">
        <Label>Photos {urls.length > 0 && <span className="text-muted-foreground font-normal">({urls.length})</span>}</Label>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowUrlInput((s) => !s)} className="rounded-sm gap-1 text-xs" data-testid={`${testIdPrefix}-add-url-toggle`}>
            <LinkIcon className="h-3 w-3" /> Paste URL
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-sm gap-1" data-testid={`${testIdPrefix}-upload-btn`}>
            <Upload className="h-3 w-3" /> Upload
          </Button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
          className="hidden"
          data-testid={`${testIdPrefix}-file-input`}
        />
      </div>

      {showUrlInput && (
        <div className="mt-2 flex gap-2">
          <Input
            placeholder="https://…"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
            className="text-sm"
            data-testid={`${testIdPrefix}-url-input`}
          />
          <Button type="button" size="sm" onClick={addUrl} className="rounded-sm" data-testid={`${testIdPrefix}-url-add`}>Add</Button>
        </div>
      )}

      <div
        onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDropFiles}
        className={`mt-2 border border-dashed rounded-sm transition-colors ${dragOver ? "border-accent bg-accent/10" : "border-soft"}`}
        data-testid={`${testIdPrefix}-dropzone`}
      >
        {urls.length === 0 && queue.length === 0 ? (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full p-8 text-center text-xs text-muted-foreground cursor-pointer"
            data-testid={`${testIdPrefix}-empty-dropzone`}
          >
            <ImageIcon className="h-6 w-6 mx-auto opacity-40 mb-2" />
            Drag &amp; drop photos here, or tap to browse.
            <span className="block mt-1 opacity-70">Photos are auto-compressed for fast loading. First photo = cover.</span>
          </button>
        ) : (
          <div className="p-2">
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid={`${testIdPrefix}-grid`}>
              {urls.map((url, i) => (
                <div
                  key={`${url}-${i}`}
                  draggable
                  onDragStart={(e) => { dragIdx.current = i; e.stopPropagation(); }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); move(dragIdx.current, i); dragIdx.current = null; }}
                  className="relative group aspect-square border border-soft overflow-hidden cursor-grab active:cursor-grabbing bg-secondary/30"
                  data-testid={`${testIdPrefix}-thumb-${i}`}
                >
                  <img src={url} alt={`photo-${i}`} className="h-full w-full object-cover pointer-events-none" />
                  {i === 0 && (
                    <span className="absolute bottom-0 inset-x-0 bg-accent text-accent-foreground text-[9px] font-semibold uppercase tracking-wide text-center py-0.5" data-testid={`${testIdPrefix}-cover-badge`}>
                      Cover
                    </span>
                  )}
                  <div className="absolute top-1 right-1 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    {i !== 0 && (
                      <button
                        type="button"
                        onClick={() => setCover(i)}
                        title="Set as cover photo"
                        className="bg-background/90 border border-soft p-2 sm:p-1.5 flex items-center justify-center"
                        data-testid={`${testIdPrefix}-set-cover-${i}`}
                      >
                        <Star className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => remove(i)}
                      title="Remove"
                      className="bg-background/90 border border-soft p-2 sm:p-1.5 flex items-center justify-center"
                      data-testid={`${testIdPrefix}-remove-${i}`}
                    >
                      <X className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                    </button>
                  </div>
                  <span className="absolute top-1 left-1 opacity-0 group-hover:opacity-70">
                    <GripVertical className="h-3 w-3 text-white drop-shadow" />
                  </span>
                </div>
              ))}
              {queue.map((it) => (
                <div key={it.id} className="relative aspect-square border border-soft bg-secondary/40 flex flex-col items-center justify-center p-2" data-testid={`${testIdPrefix}-uploading-${it.id}`}>
                  <span className={`text-[9px] truncate w-full text-center mb-1 ${it.status === "error" ? "text-destructive" : "text-muted-foreground"}`}>
                    {it.status === "error" ? "Failed" : it.status === "compressing" ? "Compressing…" : `${it.progress}%`}
                  </span>
                  <div className="w-full h-1 bg-border rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-[width] ${it.status === "error" ? "bg-destructive w-full" : "bg-accent"}`}
                      style={it.status !== "error" ? { width: `${it.status === "compressing" ? 10 : it.progress}%` } : undefined}
                    />
                  </div>
                </div>
              ))}
            </div>
            {urls.length > 1 && (
              <p className="mt-2 text-[10px] text-muted-foreground">Drag photos to reorder · tap the star to make one the cover · drop more files anywhere in this box.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
